import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronRight, Music, Heart, Users } from 'lucide-react';

const MinstrelWorshipTrack = () => {
  const navigate = useNavigate();

  const modules = [
    "The Sound of Heaven: Understanding prophetic worship and the minstrel calling",
    "Priesthood Beyond Performance: The heart posture of a true worshipper",
    "Atmospheres of Glory: Learning to steward and sustain the presence of God",
    "Identity & Intimacy: Worship as relationship, not routine",
    "Prophetic Bass Mastery: For sound carriers—technical and spiritual training",
    "Leadership in Worship: Leading others into encounter with God",
    "Spiritual Disciplines: Prayer, fasting, and the hidden life of a minstrel",
    "Psalm 68 Principle: The strategic role of worship in spiritual warfare",
    "Creative Expression: Songwriting, spontaneous worship, and the prophetic flow",
    "Character & Humility: Building a foundation that sustains anointing",
    "Ministry with Weight: Carrying presence and authority in worship",
    "Sent as a Minstrel: Walking in your calling with clarity and purpose"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-600 to-orange-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Star className="w-8 h-8 text-white" />
            </div>
            <div className="text-sm font-semibold uppercase tracking-wider">Track 2</div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Minstrel & Worship Leadership Track
          </h1>
          <p className="text-xl text-orange-100 max-w-3xl">
            A 12-month formation program for worship leaders, musicians, singers, and carriers of God's presence.
          </p>
        </div>
      </div>

      {/* Description & Theme */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Theme</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            "The Sound of Heaven, The Presence of God, The Heart of a Minstrel"
          </p>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            This track is designed for worship leaders, musicians, singers, and anyone called to carry the presence of God through sound. Over 12 months, you will be trained not just in skill, but in spirit—learning to lead worship as a priestly act, steward atmospheres of glory, and walk in the discipline and authority of a true minstrel.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-6">Program Outcomes</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Music className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Worship Beyond Performance</h4>
                <p className="text-gray-600 text-sm">Understand the priesthood of worship and develop the heart posture of a true minstrel.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Heart className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Presence Carriers</h4>
                <p className="text-gray-600 text-sm">Learn to steward and sustain atmospheres of glory through sound and spiritual discipline.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Leadership Development</h4>
                <p className="text-gray-600 text-sm">Grow in your ability to lead others into authentic encounter with God.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <Star className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Prophetic Bass Mastery</h4>
                <p className="text-gray-600 text-sm">For sound carriers—receive both technical and spiritual training in prophetic bass.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 12 Modules */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">12-Month Curriculum</h2>
          <div className="space-y-4">
            {modules.map((module, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-orange-300 transition-colors duration-200">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 font-semibold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 leading-relaxed">{module}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enrollment Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-orange-600 to-orange-500 rounded-3xl p-12 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Star className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Journey?</h2>
            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              Join the Minstrel & Worship Leadership Track and become a carrier of God's presence through sound.
            </p>
            <div className="flex items-center justify-center space-x-6 mb-8">
              <div>
                <div className="text-sm text-orange-200">Duration</div>
                <div className="text-2xl font-bold">12 Months</div>
              </div>
              <div className="w-px h-12 bg-orange-400"></div>
              <div>
                <div className="text-sm text-orange-200">Investment</div>
                <div className="text-2xl font-bold">From £120/mo</div>
                <div className="text-xs text-orange-200">or £1,200 upfront</div>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing?track=minstrel-worship')}
              className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-all duration-200 inline-flex items-center space-x-2 group"
            >
              <span>View Payment Plans</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MinstrelWorshipTrack;